import { Check, X } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";

import type { UploadStage } from "@/api/upload";
import { Button } from "@/components/ui/button";
import { useUploads } from "@/state/uploads";

function percent(record: { bytesDone: number; size: number; stage: string; done: boolean }) {
  if (record.done) return 100;
  if (record.size === 0) return 0;
  // Hashing reads the whole file, so it gets a visible share of the
  // bar (0–10%) instead of sitting still on multi-GiB files.
  if (record.stage === "hashing") {
    return Math.floor((record.bytesDone / record.size) * 10);
  }
  if (record.stage === "initiating") return 10;
  if (record.stage === "finalizing") return 95;
  // uploading
  const byteFraction = record.bytesDone / record.size;
  return 10 + Math.floor(byteFraction * 80);
}

export function UploadProgress() {
  const { t } = useTranslation();
  const headingId = useId();
  const byId = useUploads((s) => s.byId);
  const remove = useUploads((s) => s.remove);
  const records = Object.values(byId);

  if (records.length === 0) return null;

  const stageLabel = (stage: UploadStage): string => {
    switch (stage) {
      case "hashing":
        return t("upload.hashing");
      case "initiating":
        return t("upload.initiating");
      case "uploading":
        return t("upload.uploading");
      case "finalizing":
        return t("upload.finalizing");
    }
  };

  return (
    <aside
      className="fixed bottom-4 right-4 w-80 rounded-md border bg-background shadow-lg"
      aria-labelledby={headingId}
    >
      <h2 id={headingId} className="border-b px-3 py-2 text-sm font-medium">
        {t("upload.panel_title")}
      </h2>
      <ul className="divide-y">
        {records.map((r) => {
          const pct = percent(r);
          return (
            <li key={r.id} className="flex flex-col gap-1 px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{r.filename}</p>
                  {/* Stage changes (not byte progress) are announced. */}
                  <p
                    className="text-xs text-muted-foreground"
                    aria-live="polite"
                    role={r.error ? "alert" : undefined}
                  >
                    {r.done && !r.error
                      ? t("upload.complete")
                      : r.error
                        ? t("upload.failed_with_reason", { reason: r.error })
                        : stageLabel(r.stage)}
                  </p>
                </div>
                {r.done ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => remove(r.id)}
                    aria-label={t("upload.dismiss", { name: r.filename })}
                  >
                    {r.error ? (
                      <X className="h-4 w-4" aria-hidden />
                    ) : (
                      <Check className="h-4 w-4" aria-hidden />
                    )}
                  </Button>
                ) : null}
              </div>
              <div
                className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-label={r.filename}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
              >
                <div
                  className={`h-full ${r.error ? "bg-destructive" : "bg-primary"} transition-all`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
